import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const DEFAULT_SITE_NAME = 'SalesFractionPro';
const DEFAULT_IMAGE_PATH = '/myLogo.svg';
const DEFAULT_TYPE = 'website';

const normalizeBaseUrl = () => {
  const envBase = process.env.REACT_APP_SITE_URL;
  if (envBase) {
    return envBase.replace(/\/+$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, '');
  }
  return '';
};

const toAbsoluteUrl = (baseUrl, pathOrUrl = '/') => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    return pathOrUrl;
  }
  const path = pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`;
  return `${baseUrl}${path}`;
};

const setMetaTag = (name, content, isProperty = false) => {
  if (!content) return;
  const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement('meta');
    if (isProperty) {
      tag.setAttribute('property', name);
    } else {
      tag.setAttribute('name', name);
    }
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const setLinkTag = (rel, href) => {
  if (!href) return;
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute('rel', rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

const setJsonLdScript = (id, value) => {
  if (!value) return;
  let tag = document.head.querySelector(`script[data-seo-id="${id}"]`);
  if (!tag) {
    tag = document.createElement('script');
    tag.type = 'application/ld+json';
    tag.setAttribute('data-seo-id', id);
    document.head.appendChild(tag);
  }
  tag.text = JSON.stringify(value);
};

const Seo = ({ title, description, image = DEFAULT_IMAGE_PATH, type = DEFAULT_TYPE, noindex = false, jsonLd }) => {
  const location = useLocation();

  useEffect(() => {
    const baseUrl = normalizeBaseUrl();
    const canonicalUrl = toAbsoluteUrl(baseUrl, location.pathname);
    const imageUrl = toAbsoluteUrl(baseUrl, image);

    if (title) {
      document.title = title;
    }

    setLinkTag('canonical', canonicalUrl);
    setMetaTag('description', description);
    setMetaTag('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', imageUrl);
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', type, true);
    setMetaTag('og:site_name', DEFAULT_SITE_NAME, true);
    setMetaTag('og:url', canonicalUrl, true);
    setMetaTag('og:image', imageUrl, true);

    setJsonLdScript('organization', {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: DEFAULT_SITE_NAME,
      url: baseUrl,
      logo: toAbsoluteUrl(baseUrl, DEFAULT_IMAGE_PATH),
      email: 'info@salesfractionpro.com',
      areaServed: ['North America', 'Europe'],
      sameAs: [],
    });

    setJsonLdScript('website', {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: DEFAULT_SITE_NAME,
      url: baseUrl,
      inLanguage: 'en',
    });

    if (jsonLd) {
      setJsonLdScript('page', jsonLd);
    }
  }, [description, image, jsonLd, location.pathname, noindex, title, type]);

  return null;
};

export default Seo;
